
// ABOUTME: Validation utilities for input sanitization and security checks
// ABOUTME: Provides functions for email, password, URL validation and XSS prevention

export const validateEmail = (email: string): boolean => {
  if (!email || typeof email !== 'string') return false;
  const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
  return emailRegex.test(email.trim());
};

export const validatePassword = (password: string): {
  isValid: boolean;
  errors: string[];
  strength: 'weak' | 'medium' | 'strong';
} => {
  const errors: string[] = [];
  
  if (!password || typeof password !== 'string') {
    errors.push('Password is required');
    return { isValid: false, errors, strength: 'weak' };
  }
  
  if (password.length < 8) {
    errors.push('Password must be at least 8 characters long');
  }
  
  if (!/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }
  
  if (!/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }
  
  if (!/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    errors.push('Password must contain at least one special character');
  }

  const isValid = errors.length === 0;
  
  let strength: 'weak' | 'medium' | 'strong' = 'weak';
  if (password.length >= 12 && isValid) {
    strength = 'strong';
  } else if (password.length >= 8 && isValid) {
    strength = 'medium';
  }

  return { isValid, errors, strength };
};

export const validateLinkedInUrl = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  const linkedinRegex = /^https:\/\/(www\.)?linkedin\.com\/in\/[a-zA-Z0-9-]+\/?$/;
  return linkedinRegex.test(url.trim());
};

export const validatePhoneNumber = (phone: string): boolean => {
  if (!phone || typeof phone !== 'string') return false;
  
  // Remove all non-numeric characters for validation
  const cleanPhone = phone.replace(/\D/g, '');
  
  // Check if it's a valid US phone number (10 digits) or international (7-15 digits)
  if (cleanPhone.length === 10) {
    // US format: must not start with 0 or 1
    return !cleanPhone.startsWith('0') && !cleanPhone.startsWith('1');
  }
  
  // International format: 7-15 digits
  return cleanPhone.length >= 7 && cleanPhone.length <= 15;
};

export const sanitizeName = (name: string): string => {
  if (!name || typeof name !== 'string') return '';
  
  // Remove any HTML tags and limit to alphanumeric, spaces, hyphens, and apostrophes
  const sanitized = name
    .replace(/<[^>]*>/g, '')
    .replace(/[^a-zA-Z0-9\s\-']/g, '')
    .trim()
    .slice(0, 50); // Limit length
  
  // Ensure the sanitized name is at least 2 characters
  return sanitized.length >= 2 ? sanitized : '';
};

export const sanitizeText = (text: string): string => {
  if (!text || typeof text !== 'string') return '';
  
  // Basic XSS prevention - remove HTML tags and encode special characters
  return text
    .replace(/<[^>]*>/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .trim();
};

export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  if (!file || !allowedTypes || !Array.isArray(allowedTypes)) return false;
  return allowedTypes.includes(file.type);
};

export const validateFileSize = (file: File, maxSizeInMB: number): boolean => {
  if (!file || typeof maxSizeInMB !== 'number') return false;
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};
